import { type CSSProperties, type ReactNode } from 'react';

import classes from './emptyState.module.css';

type EmptyStateProps = {
  // Headline. Defaults to a generic no-results message.
  title?: string;
  // Optional supporting line below the title (e.g. "Try adjusting your search terms").
  message?: ReactNode;
  // Optional icon rendered above the title.
  icon?: ReactNode;
  // Optional call-to-action rendered below the message (e.g. a create button).
  action?: ReactNode;
  // Optional cap on the box's width (e.g. '42rem');
  // the box is centered when this is set.
  maxWidth?: CSSProperties['maxWidth'];
  className?: string;
};

/**
 * Reusable empty / no-results placeholder — a centered dashed box with a muted
 * title, optional message, icon, and action. Sibling to `ErrorState`; use it
 * for "no search results", "nothing configured yet", and similar empty lists.
 */
export const EmptyState = ({
  title = 'No results found',
  message,
  icon,
  action,
  maxWidth,
  className,
}: EmptyStateProps) => (
  <div
    className={`${classes.wrapper} ${className || ''}`}
    style={maxWidth ? { maxWidth, marginInline: 'auto' } : undefined}
  >
    {icon && <div className={classes.icon}>{icon}</div>}
    <p className={`${classes.title} text-doto`}>{title}</p>
    {message && <p className={classes.message}>{message}</p>}
    {action && <div className={classes.action}>{action}</div>}
  </div>
);
